// body-fingerprint.ts
//
// #458 observability: did the stored copy a store-check bound onto a userMessage carry the same
// text as the message we sent? Answered IN MEMORY, and only the boolean verdict is persisted
// (`storeMatchBodyEq`). The fingerprints below never leave the extension: a salted hash of a body
// is guessable offline when the salt (the messageId) sits on the same row, so none is ever logged.
// Known limit: a send whose mentions are rewritten at send time can differ from the stored copy.

import type { SendLogMetadata } from '@bt-common/scheduling';
import type { GetUnloggedWaMessageKeyIfExistInStoreResult } from '@bt-common/interfaces/whatsapp-tool-contracts.interface';

export type StoreMatchEvidence = NonNullable<GetUnloggedWaMessageKeyIfExistInStoreResult['match']>;

const FINGERPRINT_HEX_CHARS = 8;

/** Line endings and outer whitespace differ between what we compose and what the store keeps. */
function normalizeBody(body: string | null | undefined): string {
    return (body ?? '').replace(/\r\n/g, '\n').trim();
}

/** Salted SHA-256 prefix of the normalized body, or undefined when WebCrypto is unavailable. */
export async function bodyFingerprint(salt: string, body: string | null | undefined): Promise<string | undefined> {
    try {
        const subtle = globalThis.crypto?.subtle;
        if (!subtle || !salt) return undefined;
        const bytes = new TextEncoder().encode(`${salt}\0${normalizeBody(body)}`);
        const digest = new Uint8Array(await subtle.digest('SHA-256', bytes));
        let hex = '';
        for (const b of digest) hex += b.toString(16).padStart(2, '0');
        return hex.slice(0, FINGERPRINT_HEX_CHARS);
    } catch {
        return undefined;
    }
}

/** Shape of a WhatsApp message id without the id: '3EB0:22:hex' (first 4 chars : length : hex|other). */
export function messageIdFormat(rawId: string): string {
    const kind = /^[0-9A-Fa-f]+$/.test(rawId) ? 'hex' : 'other';
    return `${rawId.slice(0, 4)}:${rawId.length}:${kind}`;
}

/** The text part of a stored message: the body of a chat message, the caption of media (whose body is a thumbnail). */
function comparableText(waMessage: { type?: unknown; body?: unknown; caption?: unknown }): string | undefined {
    const raw = waMessage.type === 'chat' ? waMessage.body : waMessage.caption;
    return typeof raw === 'string' ? raw : undefined;
}

const ERROR_CODE_MAX_CHARS = 64;

/**
 * #503 why a stored copy sits at ack -1. WA Web's sendMsgRecord catch (WAWebSendMsgRecordAction)
 * sets `isSendFailure: true` on the Msg for any send throw and, for a server nack, `ack: -1`
 * plus `errorCode` (NewChatMessagesCapped 475 -> UserReachedMaxMonthlyLimitForNewMessages).
 * Guarded reads only: a hostile or absent field is dropped, it never fails the store-check.
 */
function readNackFields(waMessage: object): Pick<StoreMatchEvidence, 'errorCode' | 'isSendFailure'> {
    const out: Pick<StoreMatchEvidence, 'errorCode' | 'isSendFailure'> = {};
    try {
        const m = waMessage as { errorCode?: unknown; isSendFailure?: unknown };
        const code = m.errorCode;
        if (typeof code === 'string' && code) out.errorCode = code.slice(0, ERROR_CODE_MAX_CHARS);
        if (typeof m.isSendFailure === 'boolean') out.isSendFailure = m.isSendFailure;
    } catch {
        return {};
    }
    return out;
}

/** Evidence about the stored message a store-check matched. Never contains the body. */
export async function buildStoreMatchEvidence(
    userMessageId: string,
    waMessage: {
        id?: { id?: unknown; fromMe?: unknown };
        t?: unknown;
        timestamp?: unknown;
        type?: unknown;
        body?: unknown;
        caption?: unknown;
        errorCode?: unknown;
        isSendFailure?: unknown;
    },
): Promise<StoreMatchEvidence | undefined> {
    try {
        const rawId = waMessage.id?.id;
        if (typeof rawId !== 'string' || !rawId) return undefined;
        const t = typeof waMessage.t === 'number' ? waMessage.t : typeof waMessage.timestamp === 'number' ? waMessage.timestamp : undefined;
        const evidence: StoreMatchEvidence = { idFormat: messageIdFormat(rawId) };
        if (t !== undefined) evidence.t = t;
        if (typeof waMessage.id?.fromMe === 'boolean') evidence.fromMe = waMessage.id.fromMe;
        const bodyFp = await bodyFingerprint(userMessageId, comparableText(waMessage));
        if (bodyFp) evidence.bodyFp = bodyFp;
        Object.assign(evidence, readNackFields(waMessage));
        return evidence;
    } catch {
        return undefined;
    }
}

/**
 * The message-log metadata fields for a store-check match (empty when nothing matched).
 * `sentText` is compared with the matched copy's fingerprint here and only the boolean is returned.
 */
export async function storeMatchLogMetadata(
    match: StoreMatchEvidence | undefined,
    userMessageId: string,
    sentText: string | null | undefined,
): Promise<SendLogMetadata> {
    if (!match) return {};
    const out: SendLogMetadata = { storeMatchIdFormat: match.idFormat };
    if (match.t !== undefined) out.storeMatchT = match.t;
    if (match.fromMe !== undefined) out.storeMatchFromMe = match.fromMe;
    if (match.errorCode !== undefined) out.storeCheckErrorCode = match.errorCode;
    if (match.isSendFailure !== undefined) out.storeCheckIsSendFailure = match.isSendFailure;
    if (match.bodyFp !== undefined && typeof sentText === 'string') {
        const sentFp = await bodyFingerprint(userMessageId, sentText);
        if (sentFp !== undefined) out.storeMatchBodyEq = sentFp === match.bodyFp;
    }
    return out;
}
